/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for anypurpose without the express written permission of Honeywell. */
import { useEffect, useState } from "react";
import {
  TextField,
  Autocomplete,
  Checkbox,
  FormLabel,
  Chip,
  Grid,
  Tooltip,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import "./AutoCompleteMultiSelect.scss";
import classes from "../../components/TenantPage/FilterOptions.module.css";

interface IProps {
  options: { label: any; value: any }[] | [];
  label?: any;
  selectAllLabel?: any;
  onChange?: (event: any, selectedOps: any, reason: any) => void;
  required?: boolean;
  hasError?: boolean;
  helperText?: string;
  defaultValues?: any;
  labelFontSize?: any;
  readOnlyDefaultValueOptions?: { label: any; value: any }[] | [];
}

export default function MultiSelect(props: IProps) {
  const readOnlyDefaultValueOptions = props?.readOnlyDefaultValueOptions
    ? props.readOnlyDefaultValueOptions
    : [];
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<
    { label: any; value: any }[] | []
  >(props?.defaultValues ? props.defaultValues : []);
  const allSelected = props?.options?.length === selectedOptions?.length;

  const handleClearOptions = () => setSelectedOptions([]);

  useEffect(() => {
    if (props?.defaultValues) {
      setSelectedOptions(props?.defaultValues);
    }
  }, [props?.defaultValues]);

  /**
   * To hide the options on selecting all option
   * @param selctedOptions selected optoin list
   */ // istanbul ignore next

  const hideOptionsOnSelectAll = (selctedOptions: string | any[]) => {
    if (props?.options?.length === selctedOptions.length) {
      setShowOptions(false);
    }
  };
  // istanbul ignore next
  // handle the select all feature on change
  const handleChange = (event: any, selectedOps: any, reason: string) => {
    if (reason === "selectOption" || reason === "removeOption") {
      if (selectedOps[selectedOps.length - 1]?.value === "Select-all") {
        const nowSelected: any =
          selectedOptions?.length === props?.options?.length
            ? []
            : props.options;

        setSelectedOptions(nowSelected);
        props.onChange && props.onChange(event, nowSelected, reason);

        hideOptionsOnSelectAll(nowSelected);
      } else {
        /**Excluding Read only values from selectedOps */
        const selectedOptionList = selectedOps.filter(
          (option: any) => !readOnlyDefaultValueOptions.includes(option)
        );

        setSelectedOptions(selectedOptionList);
        props.onChange && props.onChange(event, selectedOptionList, reason);

        hideOptionsOnSelectAll(selectedOptionList);
      }
    } else if (reason === "clear") {
      handleClearOptions && handleClearOptions();
      props.onChange && props.onChange(event, [], reason);
    } else {
    }
  };
  const [displayList, setDisplayList] = useState("");

  useEffect(() => {
    const remainingOptions = selectedOptions.slice(2);

    // Extract the labels from the remaining options
    const remainingLabels = remainingOptions.map((option) => option.label);

    // Join the labels with commas
    const commaSeparatedString = remainingLabels.join(", ");

    // Update the state to display the comma-separated list
    setDisplayList(commaSeparatedString);
  }, [selectedOptions]);

  const filter = createFilterOptions();

  return (
    <>
      <FormLabel
        required={props.required}
        sx={{ fontSize: props.labelFontSize ? props.labelFontSize : "0.8rem" }}
        className="text-input-label"
      >
        {props.label}
      </FormLabel>
      <Autocomplete
        data-testid="autoinput"
        sx={{
          "--Chip-minHeight": "3px",
          "& .MuiAutocomplete-listbox": {
            padding: 0,
            paddingTop: 0,
          },
          "& .MuiInputBase-input": {
            height: "1em",
          },
          "& .MuiAutocomplete-tag": {
            marginTop: "0 !important",
            marginRight: "0vw !important",
            marginLeft: "0.5rem !important",
            fontSize: "0.7rem",
          },
          "& .MuiFormHelperText-root": {
            marginTop:
              readOnlyDefaultValueOptions &&
              readOnlyDefaultValueOptions?.length > 0
                ? "2rem !important"
                : "1",
          },
          "&.MuiAutocomplete-hasPopupIcon": {
            paddingRight: "0px",
          },

          "&.MuiAutocomplete-hasClearIcon": {
            paddingRight: "0px",
          },

          "&. MuiAutocomplete-root": {
            paddingRight: "0px",
          },
          "&. MuiOutlinedInput-root": {
            paddingRight: "0px",
          },
          ".MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon": {
            paddingRight: "0px",
          },

          //.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon.css-jz7ryk-MuiAutocomplete-root .MuiOutlinedInput-root
        }}
        id="checkboxes-tags-demo"
        className="multiselect-dropdown wrapper_multiselect"
        multiple
        disableCloseOnSelect
        options={[...props.options, ...readOnlyDefaultValueOptions]}
        getOptionDisabled={(option: any) =>
          readOnlyDefaultValueOptions.includes(option)
        }
        limitTags={2}
        value={[...selectedOptions, ...readOnlyDefaultValueOptions]}
        getOptionLabel={(option: any) => option.label}
        // renderTags={(tagValue, getTagProps) =>
        //   tagValue.map((option: any, index) =>
        //     index <= 1 ? (
        //       <Chip
        //         label={option.label}
        //         {...getTagProps({ index })}
        //         disabled={readOnlyDefaultValueOptions.includes(option)}
        //       />
        //     ) : index == 2 ? (
        //       <>
        //         <Grid sx={{ marginTop: "3vh", paddingRight: "0.5vw" }}>
        //           ...{" "}
        //         </Grid>
        //         <Grid item className="borderchip">
        //           <Tooltip title={displayList}>
        //             <Grid>+ {tagValue.length - 2}</Grid>
        //           </Tooltip>
        //         </Grid>
        //       </>
        //     ) : (
        //       <div></div>
        //     )
        //   )
        // }
        isOptionEqualToValue={(option: any, value: any) =>
          option.value === value.value
        }
        filterOptions={(options: any, params: any) => {
          const filtered = filter(options, params);
          if (params?.inputValue?.length === 0 && filtered?.length > 0) {
            const selectAllOption = {
              label: "Select all",
              value: "Select-all",
            };
            return [props.options?.length > 0 && selectAllOption, ...filtered];
          }
          return [...filtered];
        }}
        renderOption={(renderProps, option: any, { selected }) => {
          // To control the state of 'select-all' checkbox
          // istanbul ignore next
          const selectAllProps =
            option?.label === "Select all" ? { checked: allSelected } : {};

          return (
            (showOptions || !allSelected) && (
              <li
                {...renderProps}
                style={{ alignItems: "center", paddingRight: 2 }}
                className="list-dropdown"
              >
                {props.options?.length > 0 && (
                  <Checkbox
                    style={{
                      width: "3vh",
                      height: "3vh",
                      margin: "2vh",
                    }}
                    checked={selected}
                    {...selectAllProps}
                    disabled={readOnlyDefaultValueOptions.includes(option)}
                  />
                )}
                {option.label}
              </li>
            )
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            helperText={<div>{props.helperText}</div>}
            error={props.hasError}
            placeholder={
              selectedOptions?.length === 0 &&
              readOnlyDefaultValueOptions.length === 0
                ? "Select an option"
                : ""
            }
            sx={{
              // padding: ".01vh",
              "& .MuiFormHelperText-root": {
                marginLeft: "0",
              },
              "& .MuiAutocomplete-root .MuiInputBase-input": {
                height: "1em",
              },
            }}
          />
        )}
        onChange={handleChange}
        onOpen={() => {
          setShowOptions(true);
        }}
        onClose={() => {
          setShowOptions(false);
        }}
      />
    </>
  );
}
